const DEFAULT_API_BASE_URL = 'https://api.mtv-media.ru';
const AUTH_STORAGE_KEY = 'mtv.auth.session';

export const API_BASE_URL = String(
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/+$/, '');

function parseJSON(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeErrorMessage(payload, fallbackMessage) {
  if (!payload) return fallbackMessage;

  switch (payload.error) {
    case 'invalid_credentials':
      return 'Неверный логин или пароль.';
    case 'user_exists':
      return 'Пользователь с таким логином уже существует.';
    case 'email_in_use':
      return 'Этот email уже используется.';
    case 'invalid_code':
      return 'Неверный код восстановления.';
    case 'code_expired':
      return 'Срок действия кода восстановления истёк.';
    case 'unauthorized':
      return 'Сессия истекла. Выполните вход заново.';
    case 'missing_REDIS_URL':
    case 'missing_EMAIL_CONFIG':
      return 'Сервис авторизации временно недоступен.';
    default:
      return payload.message || fallbackMessage;
  }
}

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const text = await response.text();
  const payload = text ? parseJSON(text) : null;

  if (!response.ok) {
    throw new Error(
      normalizeErrorMessage(payload, 'Не удалось выполнить запрос к сервису аккаунтов.')
    );
  }

  return payload;
}

export function readStoredSession() {
  if (typeof window === 'undefined') return null;
  return parseJSON(window.localStorage.getItem(AUTH_STORAGE_KEY) || '');
}

export function writeStoredSession(session) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
}

export async function loginAccount(credentials) {
  return request('/api/auth/login', {
    method: 'POST',
    body: credentials,
  });
}

export async function registerAccount(payload) {
  return request('/api/auth/register', {
    method: 'POST',
    body: payload,
  });
}

export async function startPasswordReset(loginOrEmail) {
  return request('/api/auth/password-reset/start', {
    method: 'POST',
    body: { loginOrEmail },
  });
}

export async function confirmPasswordReset(email, code, newPassword) {
  return request('/api/auth/password-reset/confirm', {
    method: 'POST',
    body: { email, code, newPassword },
  });
}

export async function fetchCurrentUser(token) {
  return request('/api/auth/me', { token });
}

export async function fetchSessions(token) {
  return request('/api/auth/sessions', { token });
}

export async function revokeSession(token, sessionId) {
  return request('/api/auth/session/revoke', {
    method: 'POST',
    token,
    body: { sessionId },
  });
}

export async function logoutAccount(token) {
  return request('/api/auth/logout', {
    method: 'POST',
    token,
  });
}

export async function fetchNewsPosts(limit = 20, offset = 0) {
  const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.min(limit, 50)) : 20;
  const safeOffset = Number.isFinite(offset) ? Math.max(0, Math.trunc(offset)) : 0;
  return request(`/api/news?limit=${safeLimit}&offset=${safeOffset}`);
}

export async function fetchManagedUsers(token) {
  return request('/api/auth/users', { token });
}

export async function fetchManagedUser(token, login) {
  return request(`/api/auth/user?login=${encodeURIComponent(login)}`, { token });
}

export async function updateManagedUserPermissions(token, login, permissions) {
  return request('/api/auth/permissions', {
    method: 'POST',
    token,
    body: {
      login,
      permissions,
    },
  });
}
